import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { useLanguage } from '@/contexts/LanguageContext';
import { t } from '@/data/translations';
import { X } from 'lucide-react';
import axios from 'axios';
import SEOHead from '@/components/SEOHead';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function FadeIn({ children, delay = 0, className = '' }) {
  const { ref, inView } = useInView({ threshold: 0.15, triggerOnce: true });
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 20 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 1, delay, ease: [0.16, 1, 0.3, 1] }}
      className={className}
    >
      {children}
    </motion.div>
  );
}

export default function PortfolioPage() {
  const { lang } = useLanguage();
  const txt = t.portfolio[lang];
  const [images, setImages] = useState([]);
  const [filter, setFilter] = useState('all');
  const [lightbox, setLightbox] = useState(null);

  useEffect(() => {
    axios.get(`${API}/gallery`).then(res => {
      if (res.data.images) setImages(res.data.images);
    }).catch(() => {});
  }, []);

  const filtered = filter === 'all' ? images : images.filter(img => img.category === filter);

  const filters = [
    { key: 'all', label: txt.all },
    { key: 'exterior', label: txt.exterior },
    { key: 'interior', label: txt.interior },
  ];

  return (
    <main data-testid="portfolio-page" className="min-h-screen bg-alpine-mist pt-28 pb-20">
      <SEOHead page="portfolio" />
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-24">
        <FadeIn>
          <p className="text-xs uppercase tracking-[0.25em] text-warm-wood font-sans mb-4">{txt.overline}</p>
        </FadeIn>
        <FadeIn delay={0.1}>
          <h1 className="font-serif text-4xl sm:text-5xl text-charcoal tracking-tight">{txt.title}</h1>
        </FadeIn>
        <FadeIn delay={0.2}>
          <p className="text-base text-charcoal/50 font-sans mt-4 max-w-xl font-light">{txt.subtitle}</p>
        </FadeIn>

        <FadeIn delay={0.3}>
          <div className="flex gap-6 mt-10 mb-12" data-testid="portfolio-filters">
            {filters.map(f => (
              <button
                key={f.key}
                onClick={() => setFilter(f.key)}
                data-testid={`filter-${f.key}`}
                className={`text-xs uppercase tracking-[0.2em] font-sans pb-1 transition-all duration-500 ${
                  filter === f.key
                    ? 'text-charcoal border-b border-charcoal'
                    : 'text-charcoal/40 hover:text-charcoal/70'
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>
        </FadeIn>

        <div className="gallery-grid" data-testid="gallery-grid">
          {filtered.map((img, i) => (
            <FadeIn key={img.id} delay={0.1 * i}>
              <div
                className="gallery-item aspect-[4/3]"
                onClick={() => setLightbox(img)}
                data-testid={`gallery-item-${img.id}`}
              >
                <img
                  src={img.src}
                  alt={lang === 'fr' ? img.alt_fr : img.alt_en}
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
              </div>
            </FadeIn>
          ))}
        </div>
      </div>

      {lightbox && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="lightbox-overlay"
          onClick={() => setLightbox(null)}
          data-testid="lightbox-overlay"
        >
          <button
            className="absolute top-6 right-6 text-white/60 hover:text-white transition-colors z-10"
            onClick={() => setLightbox(null)}
            data-testid="lightbox-close"
          >
            <X size={28} />
          </button>
          <img
            src={lightbox.src}
            alt={lang === 'fr' ? lightbox.alt_fr : lightbox.alt_en}
          />
        </motion.div>
      )}
    </main>
  );
}
